import React, { createContext, ReactNode, useContext, useEffect, useState } from "react";
import useAxios from "../../hooks/axios/useAxios";
import { baseUrl } from "../../config/serverConfig";

// ── Types ────────────────────────────────────────────────────────────────────

type PriceTableEntry = {
  id: string;
  stairType: string;
  profileColor: string;
  material: string;
  section: string;
  pricePerUnit: number;
  displayName?: string | null;
  isActive?: boolean;
};

type PriceConfigEntry = {
  key: string;
  value: number;
  name?: string | null;
  custom?: boolean;
};

export type FixedCostEntry = {
  key: string;
  name: string;
  value: number;
};

type ConnectionTypeEntry = {
  code: string;
  displayName: string;
  imageUrl?: string | null;
  sortOrder?: number;
};

export type SpecialRiserEntry = {
  id: string;
  name: string;
  description?: string | null;
  pricePerUnit: number;
  imageUrl?: string | null;
  sortOrder?: number;
};

// Alleen de maat: een plateau rekent als één trede, de maat bepaalt de prijs niet.
export type PlateauSizeEntry = {
  id: string;
  label: string;
  sortOrder?: number;
};

/**
 * De catalogus achter de configurator: materialen, kleuren en keuzelijsten.
 *
 * Hier staan BEWUST geen prijzen meer. Elk bedrag komt uit
 * `POST /pricing/calculate` (zie PricingContext) — de frontend kan geen prijs
 * meer afleiden, ook niet bij een storing.
 */
type PricesContextValue = {
  getColors: (material: string) => string[];
  getMaterialName: (material: string) => string;
  getColorImage: (material: string, colorCode: string) => string | undefined;
  // Kiesbare materialen voor de calculator: distinct uit de (actieve)
  // PriceTable-rijen — dezelfde bron als Prijsbeheer — i.p.v. het hardcoded
  // frontend-enum, zodat verwijderde materialen verdwijnen.
  materials: string[];
  connectionTypes: ConnectionTypeEntry[];
  // Actieve speciale stootborden (losse producten, niet materiaal-gebonden).
  specialRisers: SpecialRiserEntry[];
  // Actieve plateau-maatopties (cm), gesorteerd.
  plateauSizes: PlateauSizeEntry[];
  loaded: boolean;
  /**
   * Gevuld wanneer /prices niet geladen kon worden. De configurator toont dit
   * en blokkeert opslaan: zonder kleuren uit de database kan er geen geldige
   * order worden samengesteld.
   */
  loadError: string | null;
};

// ── Geen prijs-fallbacks ─────────────────────────────────────────────────────
//
// Er staan hier bewust GEEN hardcoded prijzen of prijstabellen meer. Die
// leverden bij een storing stilzwijgend andere bedragen op dan de database, en
// sinds de verplaatsing van de berekening naar de backend kan de frontend
// sowieso geen prijs meer uitrekenen. Laadt /prices niet, dan blokkeert
// `loadError` het opslaan — liever geen bedrag dan een verzonnen bedrag.

// Materialen die de Order-kolommen daadwerkelijk accepteren (spiegel van het
// Prisma-enum `Material` in backend prisma/schema.prisma). Een PriceTable-
// materiaal buiten deze lijst zou wél kiesbaar maar níét opslaanbaar zijn
// (Prisma weigert de enum-waarde met een 422) — daarom snijden we erop.
const ORDER_MATERIAL_ENUM = [
  "HPL",
  "PVC",
  "WOVEN",
  "SENSE",
  "SLATE_LEATHER",
  "BETON",
];

function buildMaterials(entries: PriceTableEntry[]): string[] {
  return Array.from(new Set(entries.map((e) => e.material)))
    .filter((m) => ORDER_MATERIAL_ENUM.includes(m))
    .sort();
}

// ── Context ──────────────────────────────────────────────────────────────────

// Kleuren hebben BEWUST geen fallback: colorCode is een extern contract en
// een hardcoded lijst loopt onvermijdelijk uit de pas met ColorOption. Laadt
// /prices niet, dan is de kleurkeuze leeg en blokkeert loadError het opslaan —
// liever geen keuze dan een keuze die de backend weigert.
const emptyGetColors = (_material: string): string[] => [];
const fallbackGetMaterialName = (material: string): string => material;
const normColorKey = (m: string, c: string) =>
  `${m}|${c}`.toLowerCase().replace(/[\s_]+/g, "");
const fallbackGetColorImage = (): string | undefined => undefined;

function buildGetMaterialName(entries: PriceTableEntry[]) {
  const map: Record<string, string> = {};
  for (const e of entries) {
    if (e.displayName && !map[e.material]) map[e.material] = e.displayName;
  }
  return (material: string): string => map[material] ?? material;
}

const PricesContext = createContext<PricesContextValue>({
  getColors: emptyGetColors,
  getMaterialName: fallbackGetMaterialName,
  getColorImage: fallbackGetColorImage,
  materials: [],
  connectionTypes: [],
  specialRisers: [],
  plateauSizes: [],
  loaded:    false,
  loadError: null,
});

// ── Provider ─────────────────────────────────────────────────────────────────

export function PricesProvider({ children }: { children: ReactNode }) {
  const { fetchData } = useAxios({ method: "get" });
  const [getColors, setGetColors] = useState<PricesContextValue["getColors"]>(() => emptyGetColors);
  const [getMaterialName, setGetMaterialName] = useState<PricesContextValue["getMaterialName"]>(() => fallbackGetMaterialName);
  const [materials, setMaterials] = useState<string[]>([]);
  const [connectionTypes, setConnectionTypes] = useState<ConnectionTypeEntry[]>([]);
  const [specialRisers, setSpecialRisers] = useState<SpecialRiserEntry[]>([]);
  const [plateauSizes, setPlateauSizes] = useState<PlateauSizeEntry[]>([]);
  const [colorImages, setColorImages] = useState<Record<string, string>>({});
  const [loaded,    setLoaded]    = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const response = await fetchData(`${baseUrl}/prices`);

        if (!response || response.error || !response.data?.prices) {
          setLoadError(
            "De prijzen en kleuren konden niet bij de server worden opgehaald. " +
              "Ververs de pagina; blijft dit staan, neem dan contact op met Stair Solutions."
          );
        } else {
          const { prices, config: configEntries, colors: colorsMap, connectionTypes: ctRows, colorImages: ciMap, activeSpecialRisers: srRows, plateauSizes: psRows } = response.data as {
            prices: PriceTableEntry[];
            config: PriceConfigEntry[];
            colors: Record<string, string[]>;
            connectionTypes?: ConnectionTypeEntry[];
            colorImages?: Record<string, string>;
            activeSpecialRisers?: SpecialRiserEntry[];
            plateauSizes?: PlateauSizeEntry[];
          };

          const activePrices = prices.filter((p) => p.isActive !== false);
          setGetMaterialName(() => buildGetMaterialName(activePrices));
          setMaterials(buildMaterials(activePrices));
          setConnectionTypes(ctRows ?? []);
          setSpecialRisers(srRows ?? []);
          setPlateauSizes(psRows ?? []);
          setColorImages(ciMap ?? {});

          if (colorsMap && typeof colorsMap === "object") {
            setGetColors(() => (material: string) => colorsMap[material] ?? []);
          } else {
            // Prijzen geladen maar geen kleuren: de kleurkeuze zou leeg zijn,
            // dus dit is net zo goed een storing.
            setLoadError(
              "De server leverde geen kleuren mee. Ververs de pagina; blijft dit " +
                "staan, neem dan contact op met Stair Solutions."
            );
          }
        }
      } catch (e) {
        console.error("[PricesContext] /prices laden mislukt:", e);
        setLoadError(
          "De prijzen en kleuren konden niet bij de server worden opgehaald. " +
            "Ververs de pagina; blijft dit staan, neem dan contact op met Stair Solutions."
        );
      } finally {
        setLoaded(true);
      }
    };

    load();
    // eslint-disable-next-line
  }, []);

  const getColorImage = (material: string, colorCode: string) =>
    colorImages[normColorKey(material, colorCode)];

  return (
    <PricesContext.Provider value={{ getColors, getMaterialName, getColorImage, materials, connectionTypes, specialRisers, plateauSizes, loaded, loadError }}>
      {children}
    </PricesContext.Provider>
  );
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export function usePrices() {
  return useContext(PricesContext);
}

export default PricesContext;
